import { Controller } from "react-hook-form";
import { Box, FormHelperText } from "@mui/material";
import { BaseField } from "./BaseField";
import { FieldProps } from "modules/record/record-producer/types/record-producer-types";
import useTableColumns from "app/hooks/useTableColumns";
import useGetFormValueFromDependencyField from "modules/record/record-producer/hooks/useGetFormValueFromDependencyField";
import { FilterBuilder } from "./condition-field/FilterBuilder";
import Translate from "component/global/translation/translation-component";
import { useMemo } from "react";
import { useTables } from "app/hooks/useTables";
import { LogicalNode } from "modules/record/record-producer/types/query.types";
import { buildRequiredRule, getRequiredFieldMessage } from "../../services/recordProducerValidation";
import SysColumn from "app/class/sys-column";
import { getFieldHelperText } from "../../services/fieldHelperText";

const CLIENT_RUNTIME_VIRTUAL_COLUMNS: SysColumn[] = [
    new SysColumn({
        id: "__virtual_current_table_name",
        sys_table_id: "__virtual_client_runtime",
        active: true,
        label: "Current Table",
        name: "currentTableName",
        column_type: "TABLE_NAME",
        column_subtype: "",
        referenced_table_name: "",
        order: -30,
        is_display: false,
        default_value: "",
        is_hidden: false,
        is_mandatory: false,
        reference_filter: "",
        depends_on_column_id: "",
        currency_code: "",
        currency_decimal_places: null,
        created_at: new Date(),
        updated_at: new Date(),
    }),
    new SysColumn({
        id: "__virtual_is_new_record",
        sys_table_id: "__virtual_client_runtime",
        active: true,
        label: "Is New Record",
        name: "isNewRecord",
        column_type: "BOOLEAN",
        column_subtype: "",
        referenced_table_name: "",
        order: -20,
        is_display: false,
        default_value: "",
        is_hidden: false,
        is_mandatory: false,
        reference_filter: "",
        depends_on_column_id: "",
        currency_code: "",
        currency_decimal_places: null,
        created_at: new Date(),
        updated_at: new Date(),
    }),
    new SysColumn({
        id: "__virtual_roles",
        sys_table_id: "__virtual_client_runtime",
        active: true,
        label: "Roles",
        name: "roles",
        column_type: "PERMISSION_ARRAY",
        column_subtype: "",
        referenced_table_name: "",
        order: -10,
        is_display: false,
        default_value: "",
        is_hidden: false,
        is_mandatory: false,
        reference_filter: "",
        depends_on_column_id: "",
        currency_code: "",
        currency_decimal_places: null,
        created_at: new Date(),
        updated_at: new Date(),
    }),
];

export const ConditionBuilderField = ({ column, control, baseFieldProps, runtimeProps, targetTableName, formFields }: FieldProps & {
    targetTableName?: string;
    formFields?: { name: string; label: string }[];
}) => {
    const { fieldValue, column: dependencyColumn } = useGetFormValueFromDependencyField(
        column.depends_on_column_id
    );
    const { data: tables, isLoading: tablesLoading } = useTables();
    const hasDependency = Boolean(column.depends_on_column_id);
    const dependencyFieldValue = typeof fieldValue === "string" ? fieldValue : "";
    const dependencyValueMissing = fieldValue === undefined || fieldValue === null || dependencyFieldValue === "";

    const tableId = useMemo(() => {
        if (targetTableName) return tables?.find((table) => table.name === targetTableName)?.id || "";
        if (!dependencyColumn?.column_type || dependencyValueMissing) {
            return "";
        }

        if (dependencyColumn.column_type === "REFERENCE") {
            return dependencyFieldValue;
        }

        return tables?.find((t) => t.name === dependencyFieldValue)?.id || "";
    }, [dependencyColumn?.column_type, dependencyFieldValue, dependencyValueMissing, tables, targetTableName]);

    const tableColumns = useTableColumns(tableId);
    const currentTable = tables?.find((candidate) => candidate.id === column.sys_table_id);
    const supportsClientRuntimeVirtualColumns =
        column.name === "conditions" &&
        (currentTable?.name === "sys_form_action" || currentTable?.name === "sys_client_script");
    const conditionColumns = useMemo(() => {
        if (!supportsClientRuntimeVirtualColumns) {
            return tableColumns;
        }

        return [
            ...CLIENT_RUNTIME_VIRTUAL_COLUMNS,
            ...(tableColumns ?? []),
        ];
    }, [supportsClientRuntimeVirtualColumns, tableColumns]);

    if (!hasDependency && !targetTableName) {
        return (
            <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
                <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Translate text="This condition is unavailable because its dependency field is not configured. Contact an administrator." textAlign={"center"} />
                </Box>
            </BaseField>
        );
    }

    if (tablesLoading) {
        return (
            <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
                <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Translate text="Loading condition builder dependencies..." textAlign={"center"} />
                </Box>
            </BaseField>
        );
    }

    if (dependencyValueMissing && !targetTableName && !supportsClientRuntimeVirtualColumns) {
        return (
            <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
                <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Translate text="Select the dependency field to configure this condition." textAlign={"center"} />
                </Box>
            </BaseField>
        );
    }

    if (!tableId && !supportsClientRuntimeVirtualColumns) {
        return (
            <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
                <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Translate text="It was not possible to resolve the target table for this condition." color={"red"} textAlign={"center"} />
                </Box>
            </BaseField>
        );
    }

    if (!conditionColumns || conditionColumns.length === 0) {
        return (
            <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
                <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Translate text="No table columns available for this condition." color={"red"} textAlign={"center"} />
                </Box>
            </BaseField>
        );
    }

    return (
        <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
            <Controller
                name={column.name}
                control={control}
                rules={{ required: buildRequiredRule(runtimeProps?.required ?? column.is_mandatory, getRequiredFieldMessage(column)) }}
                render={({ field, fieldState }) => {
                    const handleConditionChange = (value: LogicalNode | string) => {
                        if (runtimeProps?.readonly) {
                            return;
                        }

                        field.onChange(typeof value === "string" ? value : JSON.stringify(value));
                    };
                    const helperText = getFieldHelperText(fieldState, runtimeProps);

                    return (
                        <Box>
                            <Box
                                sx={runtimeProps?.readonly ? { opacity: 0.6, pointerEvents: "none" } : undefined}
                                aria-disabled={runtimeProps?.readonly || undefined}
                            >
                                <FilterBuilder
                                    value={field.value}
                                    columns={conditionColumns}
                                    onChange={handleConditionChange}
                                    formFields={formFields}
                                />
                            </Box>
                            {helperText && (
                                <FormHelperText error={!!fieldState.error}>{helperText}</FormHelperText>
                            )}
                        </Box>
                    );
                }}
            />
        </BaseField>
    );
};
